import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, Link2, Copy, Check, Target, Globe, Brain, Search, Video, Sparkles, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { checkCombinedSections, createCombinedShareLink, analyzeJD } from '../services/api';

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || '').replace(/\/+$/, '') || (typeof window !== 'undefined' ? window.location.origin : '');

const SECTION_CONFIG = [
  { key: 'jd', label: 'JD Intelligence', icon: Target, color: 'text-indigo-500' },
  { key: 'mi', label: 'MI Analysis', icon: Globe, color: 'text-purple-500' },
  { key: 'discovery', label: 'Discovery Questions', icon: Brain, color: 'text-teal-500' },
  { key: 'sourcing', label: 'Smart Sourcing', icon: Search, color: 'text-orange-500' },
  { key: 'interview', label: 'AI Interview Questions', icon: Video, color: 'text-blue-500' },
];

const CombinedShareModal = ({ isOpen, onClose, jobId, jobTitle, onJDGenerated }) => {
  const [availability, setAvailability] = useState({});
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [generatingJD, setGeneratingJD] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen && jobId) {
      loadAvailability();
      setShareUrl(null);
      setCopied(false);
    }
  }, [isOpen, jobId]);

  const loadAvailability = async () => {
    setLoading(true);
    try {
      const data = await checkCombinedSections(jobId);
      setAvailability(data);
      // Auto-select all available
      const available = SECTION_CONFIG.filter(s => data[s.key]).map(s => s.key);
      setSelected(available);
    } catch {
      toast.error('Failed to check section availability');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (key) => {
    if (!availability[key]) return;
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    setShareUrl(null);
  };

  const handleGenerateJD = async () => {
    setGeneratingJD(true);
    try {
      await analyzeJD(jobId);
      toast.success('JD Intelligence generated!');
      setAvailability(prev => ({ ...prev, jd: true }));
      setSelected(prev => prev.includes('jd') ? prev : [...prev, 'jd']);
      if (onJDGenerated) onJDGenerated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate JD analysis');
    } finally {
      setGeneratingJD(false);
    }
  };

  const handleCreate = async () => {
    if (selected.length === 0) {
      toast.error('Select at least one section');
      return;
    }
    setCreating(true);
    try {
      const result = await createCombinedShareLink(jobId, selected);
      const url = `${PUBLIC_URL}/shared/complete/${result.share_token}`;
      setShareUrl(url);
    } catch (e) {
      toast.error('Failed to create share link');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background border rounded-xl shadow-xl w-full max-w-lg mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="combined-share-modal">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-primary/10">
            <Sparkles className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Complete Share Link</h2>
            <p className="text-xs text-muted-foreground">Choose which sections to include in one shareable link</p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <div className="space-y-2 mb-4">
              {SECTION_CONFIG.map(({ key, label, icon: Icon, color }) => {
                const available = availability[key];
                const isSelected = selected.includes(key);
                return (
                  <div key={key}
                    className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                      isSelected ? 'border-primary bg-primary/5' : available ? 'border-border hover:border-primary/30' : 'border-border/50 opacity-60'
                    }`}
                    onClick={() => toggleSection(key)}
                    data-testid={`combined-section-${key}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                        isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'
                      }`}>
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <Icon className={`h-4 w-4 ${color}`} />
                      <span className="text-sm font-medium">{label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {available ? (
                        <Badge className="bg-green-500/10 text-green-600 text-[10px]">Ready</Badge>
                      ) : key === 'jd' ? (
                        <Button variant="outline" size="sm" className="h-6 text-xs gap-1"
                          onClick={(e) => { e.stopPropagation(); handleGenerateJD(); }}
                          disabled={generatingJD} data-testid="generate-jd-inline-btn">
                          {generatingJD ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                          {generatingJD ? 'Generating...' : 'Generate'}
                        </Button>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-muted-foreground">Not generated</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {shareUrl ? (
              <div className="space-y-3">
                <div className="p-3 rounded-lg bg-green-500/5 border border-green-500/20">
                  <p className="text-xs font-medium text-green-600 mb-1.5">Link created! Share with your team:</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-background p-2 rounded border flex-1 truncate">{shareUrl}</code>
                    <Button size="sm" className="gap-1 shrink-0" onClick={handleCopy} data-testid="copy-combined-link-btn">
                      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="secondary" className="text-[10px]">{selected.length} sections</Badge>
                  <span>included in this link</span>
                </div>
              </div>
            ) : (
              <Button className="w-full gap-2" onClick={handleCreate} disabled={creating || selected.length === 0} data-testid="create-combined-link-btn">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
                {creating ? 'Creating...' : `Create Link (${selected.length} sections)`}
              </Button>
            )}

            <div className="flex justify-end mt-3">
              <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CombinedShareModal;
